import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import {
  RotateCcw,
  Clock,
  CheckCircle,
  AlertTriangle,
  Play,
  Pause,
  RefreshCw,
  Activity,
  TrendingDown,
  DollarSign,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
const ACTIONS_PER_PAGE = 5;

function RightsizingStatus({ status = {}, onRollback, onRefresh, clusterId }) {
  const [rolling_back, setRollingBack] = useState(false);
  const [actionsPage, setActionsPage] = useState(1);

  const handleRollback = async () => {
    setRollingBack(true);
    try {
      await onRollback();
    } finally {
      setRollingBack(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'idle': Clock,
      'analyzing': Activity,
      'applying': Play,
      'completed': CheckCircle,
      'failed': AlertTriangle,
      'rolling_back': RotateCcw
    };
    return icons[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colors = {
      'idle': 'text-gray-500',
      'analyzing': 'text-blue-500',
      'applying': 'text-yellow-500',
      'completed': 'text-green-500',
      'failed': 'text-red-500',
      'rolling_back': 'text-orange-500'
    };
    return colors[status] || 'text-gray-500';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return parseUtc(timestamp).toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const duration = new Date(end) - new Date(start);
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  // Helper to format CPU resources (handles K8s strings like '600m' or numbers like 0.5)
  const formatCpu = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (typeof value === 'string') return value; // Already formatted like '600m'
    if (typeof value === 'number') {
      if (value < 1) return `${Math.round(value * 1000)}m`;
      return value.toFixed(2);
    }
    return String(value);
  };

  // Helper to format memory resources (handles K8s strings like '1.2Gi' or numbers in bytes)
  const formatMemory = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (typeof value === 'string') return value; // Already formatted like '1.2Gi'
    if (typeof value === 'number') {
      const mi = value / (1024 * 1024);
      if (mi < 1024) return `${Math.round(mi)}Mi`;
      return `${(mi / 1024).toFixed(1)}Gi`;
    }
    return String(value);
  };

  // Extract CPU from resources object (handles different field names)
  const getCpu = (resources) => {
    if (!resources) return null;
    // Try different possible field names
    return resources.cpu_request || resources.cpu || resources.cpuRequest ||
      resources.requests?.cpu || resources.cpu_requests || null;
  };

  // Extract Memory from resources object (handles different field names)
  const getMemory = (resources) => {
    if (!resources) return null;
    // Try different possible field names
    return resources.memory_request || resources.memory || resources.memoryRequest ||
      resources.requests?.memory || resources.memory_requests || null;
  };

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-3"></div>
              <span className="text-lg font-semibold">Current Status</span>
            </div>
            <Button variant="outline" size="sm" onClick={onRefresh} className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-all">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                {(() => {
                  const StatusIcon = getStatusIcon(status.status);
                  return <StatusIcon className={`w-6 h-6 ${getStatusColor(status.status)}`} />;
                })()}
                <div>
                  <h3 className="text-lg font-medium capitalize">{status.status || 'Unknown'}</h3>
                  <p className="text-sm text-gray-500">
                    {status.status === 'idle' && 'No active rightsizing operations'}
                    {status.status === 'analyzing' && 'Analyzing workload patterns...'}
                    {status.status === 'applying' && 'Applying rightsizing recommendations...'}
                    {status.status === 'completed' && 'Rightsizing operation completed successfully'}
                    {status.status === 'failed' && 'Rightsizing operation failed'}
                    {status.status === 'rolling_back' && 'Rolling back previous changes...'}
                  </p>
                </div>
              </div>

              {status.current_operation && (
                <div className="space-y-2">
                  <h4 className="font-medium">Current Operation</h4>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Operation ID:</span>
                      <span className="font-mono">{status.current_operation.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Started:</span>
                      <span>{formatTimestamp(status.current_operation.started_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Workloads:</span>
                      <span>{status.current_operation.workload_count || 0}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* <div className="space-y-4"> */}
            {/*   {status.rollback_available && ( */}
            {/*     <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md"> */}
            {/*       <div className="flex items-start justify-between"> */}
            {/*         <div> */}
            {/*           <h4 className="font-medium text-yellow-800">Rollback Available</h4> */}
            {/*           <p className="text-sm text-yellow-700 mt-1"> */}
            {/*             You can rollback the last rightsizing operation if needed. */}
            {/*           </p> */}
            {/*         </div> */}
            {/*         <Button */}
            {/*           variant="outline" */}
            {/*           size="sm" */}
            {/*           onClick={handleRollback} */}
            {/*           disabled={rolling_back || status.status === 'rolling_back'} */}
            {/*           className="ml-4" */}
            {/*         > */}
            {/*           <RotateCcw className="w-4 h-4 mr-2" /> */}
            {/*           {rolling_back ? 'Rolling back...' : 'Rollback'} */}
            {/*         </Button> */}
            {/*       </div> */}
            {/*     </div> */}
            {/*   )} */}
            {/**/}
            {/*   {status.last_error && ( */}
            {/*     <div className="p-4 bg-red-50 border border-red-200 rounded-md"> */}
            {/*       <h4 className="font-medium text-red-800 flex items-center"> */}
            {/*         <AlertTriangle className="w-4 h-4 mr-2" /> */}
            {/*         Last Error */}
            {/*       </h4> */}
            {/*       <p className="text-sm text-red-700 mt-1">{status.last_error}</p> */}
            {/*     </div> */}
            {/*   )} */}
            {/* </div> */}
          </div>
        </CardContent>
      </Card>

      {/* Recent Actions */}
      {status.recent_actions && status.recent_actions.length > 0 && (() => {
        const totalActions = status.recent_actions.length;
        const totalPages = Math.ceil(totalActions / ACTIONS_PER_PAGE);
        const safePage = Math.min(actionsPage, totalPages);
        const startIdx = (safePage - 1) * ACTIONS_PER_PAGE;
        const paginatedActions = status.recent_actions.slice(startIdx, startIdx + ACTIONS_PER_PAGE);

        return (
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full mr-3"></div>
                <CardTitle className="text-lg font-semibold">Recent Actions</CardTitle>
                <span className="ml-2 text-sm text-gray-500">({totalActions})</span>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" onClick={() => setActionsPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} className="h-8 w-8 p-0">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">{safePage} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setActionsPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="h-8 w-8 p-0">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {paginatedActions.map((action, index) => (
                <div key={index} className="border border-slate-200 rounded-xl p-5 bg-gradient-to-r from-white to-slate-50 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {(() => {
                          const StatusIcon = action.status === 'completed' ? CheckCircle : AlertTriangle;
                          return <StatusIcon className={`w-5 h-5 ${action.status === 'completed' ? 'text-green-500' : 'text-red-500'}`} />;
                        })()}
                        <div>
                          <h4 className="font-medium">{action.workload_name}</h4>
                          <p className="text-sm text-gray-500">{action.namespace}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Current: </span>
                          <span className="font-mono">
                            {formatCpu(getCpu(action.current_resources))} /
                            {formatMemory(getMemory(action.current_resources))}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Applied: </span>
                          <span className="font-mono text-green-600">
                            {formatCpu(getCpu(action.recommended_resources))} /
                            {formatMemory(getMemory(action.recommended_resources))}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {formatTimestamp(action.completed_at)}
                      </div>
                    </div>
                    {action.can_rollback && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRollback(action.action_id)}
                        disabled={rolling_back}
                        className="ml-4"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Rollback
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Operation History */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center">
            <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full mr-3"></div>
            <CardTitle className="text-lg font-semibold">Operation History</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {status.history && status.history.length > 0 ? (
            <div className="space-y-4">
              {status.history.map((operation, index) => (
                <div key={index} className="border border-slate-200 rounded-xl p-5 bg-gradient-to-r from-white to-slate-50 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {(() => {
                        const StatusIcon = getStatusIcon(operation.status);
                        return <StatusIcon className={`w-5 h-5 ${getStatusColor(operation.status)}`} />;
                      })()}
                      <div>
                        <h4 className="font-medium">Operation #{operation.id}</h4>
                        <p className="text-sm text-gray-500 capitalize">{operation.type || 'rightsizing'}</p>
                      </div>
                    </div>
                    <Badge className={operation.status === 'completed' ? 'bg-green-100 text-green-800' :
                      operation.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'}>
                      {operation.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Started:</span>
                      <div className="font-medium">{formatTimestamp(operation.started_at)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Completed:</span>
                      <div className="font-medium">{formatTimestamp(operation.completed_at)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Duration:</span>
                      <div className="font-medium">
                        {formatDuration(operation.started_at, operation.completed_at)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Workloads:</span>
                      <div className="font-medium">{operation.workload_count || 0}</div>
                    </div>
                  </div>

                  {operation.summary && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center space-x-2">
                        <TrendingDown className="w-4 h-4 text-green-500" />
                        <div>
                          <div className="text-sm text-gray-600">CPU Reduction</div>
                          <div className="font-medium">{operation.summary.cpu_reduction || '0%'}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <TrendingDown className="w-4 h-4 text-blue-500" />
                        <div>
                          <div className="text-sm text-gray-600">Memory Reduction</div>
                          <div className="font-medium">{operation.summary.memory_reduction || '0%'}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-green-500" />
                        <div>
                          <div className="text-sm text-gray-600">Monthly Savings</div>
                          <div className="font-medium">${operation.summary.monthly_savings || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {operation.error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <h5 className="text-sm font-medium text-red-800">Error Details</h5>
                      <p className="text-sm text-red-700 mt-1">{operation.error}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Operation History</h3>
              <p className="text-gray-500">
                Rightsizing operations will appear here once they are executed.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default RightsizingStatus;
